////////////////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2023 Autodesk, Inc. All rights reserved.
//
//                      ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
////////////////////////////////////////////////////////////////////////////////

import React from 'react';
import {FileUI} from "../dataModel/FileUI";
import {ZipEntryUI} from "../dataModel/ZipEntryUI";
import {DirectoryUI} from "../dataModel/DirectoryUI";
import {ConvertFileIcon} from "../converters/ConvertFileIcon";
import {BIM360ItemBase} from "../dataModel/BIM360ItemBase";
import {
  FlexRow,
  FlexRowCentered,
  FolderBlueRecursive,
  FolderBlueSingle
} from "../CommonStyledComponents";
import {ConvertFileSize} from "../converters/ConvertFileSize";
import {ConvertRunDate} from "../converters/ConvertRunDate";
import {
  ExtractLocationType,
  FileDestinationNamingType,
  JobRunItemStatusType
} from "../clients/Classes";
import {DownloadUrl} from "../Utility";
import {ExtractionService} from "../services/ExtractionService";
import Theme from "@adsk/alloy-react-theme";
import {
  AlertErrorIcon,
  CheckmarkIcon,
  CloudIcon,
  CloudSharedIcon,
  DownloadIcon,
  FolderIcon,
  FunnelIcon
} from "@adsk/alloy-react-icon";
import {IconButton} from "@adsk/alloy-react-button";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import Checkbox from "@adsk/alloy-react-checkbox";
import Tooltip from "@adsk/alloy-react-tooltip";
import {CloudFileType} from "../clients/V2Classes";
import {IconBadge} from "@adsk/alloy-react-badge";

const extractService = new ExtractionService();

const TreeNodeFileContent = (
  {
    item,
    treeNodeProps,
    allowSelection,
    allowMultiSelection,
    allowDownload,
    isDirectoryIncluded,
    onSetOptions,
    onError
  }: {
    item: BIM360ItemBase,
    treeNodeProps: any,
    allowSelection: boolean,
    allowMultiSelection: boolean,
    allowDownload: boolean,
    isDirectoryIncluded?: boolean,
    onSetOptions?: (item: DirectoryUI) => void,
    onError?: (error: any, operation: string) => void
  }) => {

  function resetFile(file: FileUI | ZipEntryUI): void {
    file.IsExtracting = true;
    file.DownloadLink = undefined;
    file.DownloadError = undefined;
    file.ExtractComplete = false;
  }

  function downloadClick(e: React.MouseEvent, item: FileUI | ZipEntryUI): void {
    e.stopPropagation();

    let fileId: string | undefined;
    let zipName: string | undefined;
    if (item instanceof FileUI) {
      fileId = item.Id;
      if (item.SubItems != null && item.SubItems.length > 0) {
        item.SubItems.forEach(z => resetFile(z));
      }
    } else {
      fileId = item.ParentId;
      zipName = item.Name;
    }

    resetFile(item);

    extractService.StartExtraction(
      item.HubId!,
      item.ProjectId,
      fileId!,
      ExtractLocationType.DirectDownload,
      item.StorageType,
      FileDestinationNamingType.AppendTimestamp,
      zipName)
      .then(result => {
        let completions = 0;
        const targetCompletions = result.length;
        result.forEach(r => {
          const extractingFile = item;
          const startTime = new Date();
          const timerId = setInterval(() => {
            extractService.GetStatus(r.jobId!)
              .then(value => {
                if (value.status === JobRunItemStatusType.Success) {
                  completions++;
                  if (completions === targetCompletions) {
                    item.IsExtracting = false;
                    item.ExtractComplete = true;
                  }
                  clearInterval(timerId);
                  const endTime = new Date();
                  const elapsedSeconds = Math.abs(endTime.getTime() - startTime.getTime()) / 1000;
                  console.log(`Completed Extract of ${value.filename} in ${elapsedSeconds} seconds`);
                  if (extractingFile instanceof FileUI
                    && extractingFile.SubItems != null
                    && extractingFile.SubItems.length > 0) {
                    const entry = extractingFile.SubItems.find(s => s.Name === value.filename);
                    if (entry != null) {
                      entry.IsExtracting = false;
                      entry.ExtractComplete = true;
                      entry.DownloadLink = value.downloadUrl;
                    }
                  } else {
                    extractingFile.IsExtracting = false;
                    extractingFile.ExtractComplete = true;
                    extractingFile.DownloadLink = value.downloadUrl;
                  }

                  if (value.extractLocationType === ExtractLocationType.DirectDownload) {
                    DownloadUrl(value.downloadUrl!, value.filename!);
                  }
                } else if (value.status === JobRunItemStatusType.Error) {
                  completions++;
                  if (completions === targetCompletions) {
                    item.IsExtracting = false;
                  }
                  clearInterval(timerId);
                  const endTime = new Date();
                  const elapsedSeconds = Math.abs(endTime.getTime() - startTime.getTime()) / 1000;
                  console.log(`Completed Extract of ${r.filename} with errors in ${elapsedSeconds} seconds`);
                  console.error(value);
                  extractingFile.DownloadError = `There was an error extracting: ${value.messages!.join(', ')}`;
                  extractingFile.IsExtracting = false;
                }
              })
              .catch(error => {
                if (onError) {
                  onError(error, 'Get Status');
                }
              });
          }, 10000);
        });
      })
      .catch(error => {
        if (onError) {
          onError(error, 'Extract File');
        }
        item.IsExtracting = false;
      });
  }

  function menuAnchorClick(): void {
    if (item.ForceCheckState != null || !(item instanceof DirectoryUI) || !onSetOptions) {
      return;
    }
    onSetOptions(item);
  }

  const folderIconStyle = item.ForceCheckState == null
    ? {marginRight: '0.5em', width: '24px', height: '24px', gridArea: '1/1'}
    : {marginRight: '0.5em', width: '24px', height: '24px', color: 'gray', cursor: 'not-allowed', gridArea: '1/1'}

  return (
    <FlexRowCentered>
      {
        allowSelection && allowMultiSelection && (item instanceof FileUI || item instanceof ZipEntryUI) &&
        <Checkbox
          disabled={item.ForceCheckState != null}
          checked={item.ForceCheckState !== false && (treeNodeProps.isSelected || item.ForceCheckState === true)}
          style={{marginRight: '0.5em'}}/>
      }
      {
        allowSelection && allowMultiSelection && item instanceof DirectoryUI &&
        <div style={{marginRight: '0.5em'}} onClick={menuAnchorClick}>
          {
            isDirectoryIncluded && item.IsRecursive && item.IsDynamic &&
            <Tooltip content={'Folder and all nested folders are included in the task'}>
              <div style={{display: 'grid'}}>
                <FolderBlueRecursive style={folderIconStyle}/>
                {
                  item.FileTypeFilter != null && item.FileTypeFilter.length > 0 &&
                  <IconBadge
                    renderIcon={() => <FunnelIcon color={'white'} size={10}/>}
                    style={{gridArea: '1/1', alignSelf: 'end', justifySelf: 'end'}}
                    color={Theme.colors.yellowOrange500}
                    size={'small'}/>
                }
              </div>
            </Tooltip>
          }
          {
            isDirectoryIncluded && (!item.IsDynamic || !item.IsRecursive) &&
            <Tooltip content={'Folder is included in the task'}>
              <div style={{display: 'grid'}}>
                <FolderBlueSingle style={folderIconStyle}/>
                {
                  item.IsDynamic && item.FileTypeFilter != null && item.FileTypeFilter.length > 0 &&
                  <IconBadge
                    renderIcon={() => <FunnelIcon color={'white'} size={10}/>}
                    style={{gridArea: '1/1', alignSelf: 'end', justifySelf: 'end'}}
                    color={Theme.colors.yellowOrange500}
                    size={'small'}/>
                }
              </div>
            </Tooltip>
          }
          {
            !isDirectoryIncluded &&
            <Tooltip content={'Folder is not included in the task'}>
              <FolderIcon style={folderIconStyle}/>
            </Tooltip>
          }
        </div>
      }
      {item instanceof DirectoryUI && !allowMultiSelection && <FolderIcon style={{marginRight: '0.5em'}}/>}
      {
        !(item instanceof DirectoryUI) &&
        <div style={{
          marginRight: '0.5em',
          width: '24px',
          height: '24px',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img
            src={ConvertFileIcon.Convert(item as BIM360ItemBase)}
            alt={'File'}
            style={{width: '20px', height: '20px', objectFit: 'contain'}}/>
        </div>
      }
      {
        allowDownload && !(item instanceof DirectoryUI) &&
        <Tooltip content={
          item instanceof ZipEntryUI
            ? 'Extract and download this model only'
            : item instanceof FileUI && item.IsComposite
              ? 'Extract and download this entire model and all referenced models'
              : 'Download this file'
        }>
          <IconButton renderIcon={() => <DownloadIcon/>}
                      style={{marginRight: '0.5em'}}
                      onClick={e => {
                        if (!(item instanceof FileUI) && !(item instanceof ZipEntryUI)) {
                          return;
                        }
                        downloadClick(e, item)
                      }}
                      onMouseDown={e => e.stopPropagation()}/>
        </Tooltip>
      }
      <FlexRow style={{alignItems: 'baseline'}}>
        {
          <span>{treeNodeProps.label}</span>
        }
        {
          (item instanceof FileUI || item instanceof ZipEntryUI) &&
          <FlexRowCentered style={{marginLeft: '1em'}}>
                      <span style={Theme.typography.labelSmall}>
                        {ConvertFileSize.Convert(item.FileSize!)} - Updated {ConvertRunDate.Convert(item.LastChange)}
                      </span>
            {
              item.DownloadError != null && item.DownloadError !== '' &&
              <Tooltip content={item.DownloadError}>
                <AlertErrorIcon style={{height: '1em', width: '1em', marginLeft: '1em'}}/>
              </Tooltip>
            }
            {
              item.ExtractComplete &&
              <CheckmarkIcon style={{height: '1em', width: '1em', marginLeft: '1em'}}/>
            }
            {
              item.IsExtracting &&
              <Tooltip content={'Current extracting, this may take several minutes for large files...'}>
                <ProgressRing style={{height: '1em', width: '1em', marginLeft: '1em'}}/>
              </Tooltip>
            }
            {
              item.DownloadLink != null && item.DownloadLink !== '' &&
              <FlexRowCentered style={{marginLeft: '1em'}}>
                <a
                  style={Theme.typography.labelSmall}
                  href={item.DownloadLink}
                  target='_blank'
                  rel={'noreferrer'}>Download</a>
              </FlexRowCentered>
            }
            {item instanceof FileUI && item.CloudFileType == CloudFileType.Cloud &&
              <Tooltip content={'Cloud Model'}>
                <CloudIcon style={{marginLeft: '0.5em'}}/>
              </Tooltip>
            }
            {item instanceof FileUI && item.CloudFileType == CloudFileType.CloudWorkshared &&
              <Tooltip content={'Workshared cloud model'}>
                <CloudSharedIcon style={{marginLeft: '0.5em'}}/>
              </Tooltip>
            }
          </FlexRowCentered>}
      </FlexRow>
    </FlexRowCentered>
  );
};

export default TreeNodeFileContent;